import {NodeKey} from 'lexical';
import {CSSProperties, useCallback, useEffect, useState} from 'react';

import {BUTTON_SIZE} from './utils';

type TableInfo = {
  tableElement: HTMLElement;
  tableKey: NodeKey;
  enableAddRows: boolean;
  enableAddColumns: boolean;
};

/**
 * Calculates and updates button positions for table row/column handles.
 * Uses absolute positioning relative to the anchor element.
 * Since buttons are portaled inside the scroll container, they scroll naturally with content.
 */
export function useTableButtonPositioning(
  tableInfo: TableInfo | null,
  isTemplateMode: boolean,
  anchorElem: HTMLElement
) {
  const [addColumnButtonStyle, setAddColumnButtonStyle] = useState<CSSProperties | null>(null);
  const [addRowButtonStyle, setAddRowButtonStyle] = useState<CSSProperties | null>(null);
  const [columnHandleStyles, setColumnHandleStyles] = useState<CSSProperties[]>([]);
  const [rowHandleStyles, setRowHandleStyles] = useState<CSSProperties[]>([]);

  const updatePositions = useCallback(() => {
    if (!tableInfo || !anchorElem) {
      setAddColumnButtonStyle(null);
      setAddRowButtonStyle(null);
      setColumnHandleStyles([]);
      setRowHandleStyles([]);
      return;
    }

    const tableRect = tableInfo.tableElement.getBoundingClientRect();
    const anchorRect = anchorElem.getBoundingClientRect();

    // Get the scrollable wrapper element which has overflow-x: auto and clips the table
    // Table structure: scrollableWrapper > shadowWrapper > table
    // So we need the grandparent, not the direct parent
    const shadowWrapper = tableInfo.tableElement.parentElement;
    const wrapperElement = shadowWrapper?.parentElement;
    const wrapperRect = wrapperElement?.getBoundingClientRect();

    // Calculate the visible bounds - wrapper clips the table on both sides
    const visibleLeft = wrapperRect ? Math.max(tableRect.left, wrapperRect.left) : tableRect.left;
    const visibleRight = wrapperRect
      ? Math.min(tableRect.right, wrapperRect.right)
      : tableRect.right;
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);

    // Calculate positions relative to anchor element
    const relTop = tableRect.top - anchorRect.top;
    const relVisibleLeft = visibleLeft - anchorRect.left;

    // Position add column button on the right edge of the VISIBLE area
    setAddColumnButtonStyle({
      position: 'absolute',
      top: 0,
      left: 0,
      transform: `translate(${relVisibleLeft + visibleWidth}px, ${relTop - BUTTON_SIZE}px)`,
      width: BUTTON_SIZE,
      height: tableRect.height + BUTTON_SIZE,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius:
        !isTemplateMode && !tableInfo.enableAddRows && tableInfo.enableAddColumns ? 8 : 0,
      borderBottom: '1px solid var(--dig-color__border__subtle)',
      cursor: 'e-resize',
    });

    // Position add row button on the bottom edge, width matches visible area
    const showAddColumn = isTemplateMode || tableInfo.enableAddColumns;
    setAddRowButtonStyle({
      position: 'absolute',
      top: 0,
      left: 0,
      transform: `translate(${relVisibleLeft - BUTTON_SIZE}px, ${relTop + tableRect.height}px)`,
      width: visibleWidth + BUTTON_SIZE * (showAddColumn ? 2 : 1),
      height: BUTTON_SIZE,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      cursor: 's-resize',
    });

    // Calculate column handle positions (top of each column)
    // Clip to visible area on both left and right
    const firstRow = tableInfo.tableElement.querySelector('tr:first-of-type');
    if (firstRow) {
      const cells = firstRow.querySelectorAll('th, td');
      const newColumnHandleStyles: CSSProperties[] = [];
      cells.forEach((cell, index) => {
        const cellRect = cell.getBoundingClientRect();

        // Clip cell to visible area on both sides
        const cellVisibleLeft = Math.max(cellRect.left, visibleLeft);
        const cellVisibleRight = Math.min(cellRect.right, visibleRight);
        const cellVisibleWidth = Math.max(0, cellVisibleRight - cellVisibleLeft);
        const isVisible = cellVisibleWidth > 0;

        const cellRelVisibleLeft = cellVisibleLeft - anchorRect.left;
        newColumnHandleStyles.push({
          position: 'absolute',
          top: 0,
          left: 0,
          transform: `translate(${cellRelVisibleLeft}px, ${relTop - BUTTON_SIZE}px)`,
          width: cellVisibleWidth,
          height: BUTTON_SIZE,
          borderRight:
            isVisible && cellRect.right <= visibleRight
              ? '1px solid var(--dig-color__border__subtle)'
              : 'none',
          borderRadius: 0,
          borderTopLeftRadius:
            !isTemplateMode &&
            !tableInfo.enableAddRows &&
            tableInfo.enableAddColumns &&
            index === 0 &&
            cellRect.left >= visibleLeft // Only round if cell's left edge is visible
              ? 8
              : 0,
          cursor: 'pointer',
          visibility: isVisible ? 'visible' : 'hidden',
        });
      });
      setColumnHandleStyles(newColumnHandleStyles);
    }

    // Calculate row handle positions (left of each row)
    const rows = tableInfo.tableElement.querySelectorAll('tr');
    const newRowHandleStyles: CSSProperties[] = [];
    rows.forEach((row, index) => {
      const rowRect = row.getBoundingClientRect();
      const rowRelTop = rowRect.top - anchorRect.top;
      newRowHandleStyles.push({
        position: 'absolute',
        top: 0,
        left: 0,
        transform: `translate(${relVisibleLeft - BUTTON_SIZE}px, ${rowRelTop}px)`,
        width: BUTTON_SIZE,
        height: rowRect.height,
        borderRadius: 0,
        borderTopLeftRadius:
          !isTemplateMode && tableInfo.enableAddRows && !tableInfo.enableAddColumns && index === 0
            ? 8
            : 0,
        borderBottom: '1px solid var(--dig-color__border__subtle)',
        cursor: 'pointer',
      });
    });
    setRowHandleStyles(newRowHandleStyles);
  }, [tableInfo, isTemplateMode, anchorElem]);

  // Update button positions when table info changes or on zoom/resize
  useEffect(() => {
    updatePositions();

    let rafId: number | null = null;

    // Use requestAnimationFrame to ensure layout is updated before recalculating positions
    // This also debounces multiple rapid events during zoom
    const handleZoomOrResize = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updatePositions();
        rafId = null;
      });
    };

    // Use ResizeObserver to catch all layout changes (zoom, resize, content changes)
    const resizeObserver = new ResizeObserver(handleZoomOrResize);
    if (tableInfo?.tableElement) {
      resizeObserver.observe(tableInfo.tableElement);
    }
    if (anchorElem) {
      resizeObserver.observe(anchorElem);
    }

    // Listen for scroll events on the scrollable wrapper (for horizontal scroll)
    // Table structure: scrollableWrapper > shadowWrapper > table
    const shadowWrapper = tableInfo?.tableElement?.parentElement;
    const wrapperElement = shadowWrapper?.parentElement;
    if (wrapperElement) {
      wrapperElement.addEventListener('scroll', handleZoomOrResize);
      resizeObserver.observe(wrapperElement);
    }

    // Listen for zoom changes via visualViewport
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', handleZoomOrResize);
      viewport.addEventListener('scroll', handleZoomOrResize);
    }

    // Also listen for window resize as a fallback
    window.addEventListener('resize', handleZoomOrResize);

    // Listen for browser zoom (cmd+/-) via matchMedia on resolution changes
    // This creates a media query that matches the current device pixel ratio
    // When zoom changes, the pixel ratio changes and the query no longer matches
    let currentDpr = window.devicePixelRatio;
    let zoomMediaQuery = window.matchMedia(`(resolution: ${currentDpr}dppx)`);
    const handleZoomChange = () => {
      if (currentDpr !== window.devicePixelRatio) {
        currentDpr = window.devicePixelRatio;
        handleZoomOrResize();
        // Re-create media query with new pixel ratio
        zoomMediaQuery.removeEventListener('change', handleZoomChange);
        zoomMediaQuery = window.matchMedia(`(resolution: ${currentDpr}dppx)`);
        zoomMediaQuery.addEventListener('change', handleZoomChange);
      }
    };
    zoomMediaQuery.addEventListener('change', handleZoomChange);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      if (wrapperElement) {
        wrapperElement.removeEventListener('scroll', handleZoomOrResize);
      }
      if (viewport) {
        viewport.removeEventListener('resize', handleZoomOrResize);
        viewport.removeEventListener('scroll', handleZoomOrResize);
      }
      window.removeEventListener('resize', handleZoomOrResize);
      zoomMediaQuery.removeEventListener('change', handleZoomChange);
    };
  }, [updatePositions, tableInfo?.tableElement, anchorElem]);

  // Toggle border radius based on selection state
  useEffect(() => {
    if (!tableInfo) return;

    const tableEl = tableInfo.tableElement;
    const allCells = tableEl.querySelectorAll('th, td');

    const shouldSkipFirstTh =
      !isTemplateMode && !tableInfo.enableAddRows && !tableInfo.enableAddColumns;
    const shouldSkipLastTh = !isTemplateMode && !tableInfo.enableAddColumns;
    const allThs = tableEl.querySelectorAll('th');
    const lastTh = allThs[allThs.length - 1];

    const shouldSkipFirstTdInLastRow = !isTemplateMode && !tableInfo.enableAddRows;
    const shouldSkipLastTdInLastRow =
      !isTemplateMode && !tableInfo.enableAddRows && !tableInfo.enableAddColumns;
    const allTrs = tableEl.querySelectorAll('tr');
    const firstTdInLastRow = allTrs[allTrs.length - 1].querySelector('td');
    const lastTdInLastRow = allTrs[allTrs.length - 1].querySelector('td:last-of-type');

    // Remove border radius when table is active
    allCells.forEach((cell, index) => {
      if (shouldSkipFirstTh && index === 0) return;
      if (shouldSkipLastTh && cell === lastTh) return;
      if (shouldSkipFirstTdInLastRow && cell === firstTdInLastRow) return;
      if (shouldSkipLastTdInLastRow && cell === lastTdInLastRow) return;

      const htmlCell = cell as HTMLElement;
      htmlCell.style.setProperty('border-radius', '0', 'important');
    });

    // Cleanup: restore border radius when leaving the table
    return () => {
      allCells.forEach((cell, index) => {
        if (shouldSkipFirstTh && index === 0) return;
        if (shouldSkipLastTh && cell === lastTh) return;
        if (shouldSkipFirstTdInLastRow && cell === firstTdInLastRow) return;
        if (shouldSkipLastTdInLastRow && cell === lastTdInLastRow) return;

        const htmlCell = cell as HTMLElement;
        htmlCell.style.removeProperty('border-radius');
      });
    };
  }, [tableInfo, isTemplateMode]);

  return {
    addColumnButtonStyle,
    addRowButtonStyle,
    columnHandleStyles,
    rowHandleStyles,
  };
}
